import { getRemoteCssUrl } from '../internal/module-federation/runtime';
import enviro from 'enviro';
export const GRANITE_REMOTE_CSS_ATTR = 'data-granite-remote-css';
const LOAD_TIMEOUT = 10000;
export class RemoteCssManager {
  constructor() {
    this._activeRemoteName = null;
    this._loadState = {};
  }
  get activeRemoteName() {
    return this._activeRemoteName;
  }
  switchTo(remoteName) {
    this._activeRemoteName = remoteName;
    const state = this._loadState[remoteName];
    if ((state === null || state === void 0 ? void 0 : state.status) === 'loaded') {
      this._applyActiveStyles(remoteName);
      return Promise.resolve();
    }
    if ((state === null || state === void 0 ? void 0 : state.status) === 'loading') {
      return state.promise;
    }
    if ((state === null || state === void 0 ? void 0 : state.status) === 'errored') {
      document.head.querySelectorAll(`link[${GRANITE_REMOTE_CSS_ATTR}="${remoteName}"]`).forEach(link => link.remove());
    }
    const existing = this._findRemoteCssLink(remoteName);
    if (existing !== null && existing !== void 0 && existing.sheet) {
      this._loadState[remoteName] = {
        status: 'loaded'
      };
      this._applyActiveStyles(remoteName);
      return Promise.resolve();
    }
    const link = existing !== null && existing !== void 0 ? existing : this._createRemoteCssLink(remoteName);
    if (!link) {
      this._applyActiveStyles(remoteName);
      return Promise.resolve();
    }
    return this._loadLink(link, remoteName);
  }
  deactivate(remoteName) {
    if (this._activeRemoteName === remoteName) {
      this._activeRemoteName = null;
    }
    this._disableStylesFor(remoteName);
  }
  resetForTesting() {
    this._activeRemoteName = null;
    for (const key of Object.keys(this._loadState)) {
      delete this._loadState[key];
    }
  }
  _applyActiveStyles(remoteName) {
    document.head.querySelectorAll(`link[${GRANITE_REMOTE_CSS_ATTR}]`).forEach(link => {
      if (link.sheet) {
        link.sheet.disabled = link.getAttribute(GRANITE_REMOTE_CSS_ATTR) !== remoteName;
      }
    });
  }
  _disableStylesFor(remoteName) {
    document.head.querySelectorAll(`link[${GRANITE_REMOTE_CSS_ATTR}="${remoteName}"]`).forEach(link => {
      if (link.sheet) {
        link.sheet.disabled = true;
      }
    });
  }
  _findRemoteCssLink(remoteName) {
    return document.head.querySelector(`link[${GRANITE_REMOTE_CSS_ATTR}="${remoteName}"]`);
  }
  _createRemoteCssLink(remoteName) {
    const cssUrl = getRemoteCssUrl(remoteName);
    if (!cssUrl) {
      return undefined;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    link.setAttribute(GRANITE_REMOTE_CSS_ATTR, remoteName);
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
    return link;
  }
  _loadLink(link, remoteName) {
    let resolveLoad;
    const promise = new Promise(res => {
      resolveLoad = res;
    });
    this._loadState[remoteName] = {
      status: 'loading',
      resolve: resolveLoad,
      promise
    };
    const onComplete = () => {
      link.onload = null;
      link.onerror = null;
      const current = this._loadState[remoteName];
      if ((current === null || current === void 0 ? void 0 : current.status) !== 'loading') return;
      const {
        resolve
      } = current;
      if (link.sheet) {
        this._loadState[remoteName] = {
          status: 'loaded'
        };
      } else {
        this._loadState[remoteName] = {
          status: 'errored'
        };
        link.remove();
        if (!enviro.deployed()) {
          console.error(`Failed to load CSS for remote "${remoteName}": ${link.href}`);
        }
      }
      if (this._activeRemoteName === remoteName) {
        this._applyActiveStyles(remoteName);
      }
      resolve();
    };
    const timeout = setTimeout(onComplete, LOAD_TIMEOUT);
    link.onload = () => {
      clearTimeout(timeout);
      onComplete();
    };
    link.onerror = () => {
      clearTimeout(timeout);
      onComplete();
    };
    return promise;
  }
}